import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { AxiosError } from 'axios';

import {
  API_V3,
  ApiV3Response,
  Environment,
  getSomethingPaginated,
  useSomethingLazily,
} from './types';

/**
 * Returns a specified project for the given account, provided the user has access.
 */
export const useProject = ({
  accountId,
  projectId,
  enabled = true,
}: {
  accountId?: number | null;
  projectId?: number | null;
  enabled?: boolean;
}) => {
  return useQuery<Project, AxiosError>({
    queryKey: ['project', { accountId, projectId }],
    queryFn: () => getProject(accountId ?? 0, projectId ?? 0),
    enabled: enabled && !!accountId && !!projectId,
  });
};

/**
 * Returns all projects for the given account that the user has access to.
 */
export const useProjects = ({ accountId }: { accountId?: number }) => {
  const enabled = !!accountId;
  return useQuery<Project[], AxiosError>({
    queryKey: ['projects', { accountId }],
    queryFn: () => getProjects(accountId ?? 0, undefined),
    enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export interface ProjectsParams {
  accountId?: number;
  first?: number;
  after?: string;
  includeRelated?: string[];
}

export const useProjectsLazy = () => {
  const [projects, queryExecute] = useSomethingLazily();

  const executeQuery = async (args: ProjectsParams) => {
    const res = await queryExecute({
      api_endpoint: API_V3,
      endpoint: `/accounts/${args.accountId ?? 0}/projects/`,
      params: {
        include_related: args.includeRelated
          ? JSON.stringify(args.includeRelated)
          : undefined,
        order_by: 'name',
      },
      first: args.first,
      after: args.after,
    });

    return {
      ...res,
      data: (res.data ? getResults(res.data) : []) as Project[],
      pageInfo: res.pageInfo,
      fetching: false,
    };
  };

  return [
    {
      ...projects,
      data: projects.data ? getResults(projects.data) : [],
      pageInfo: projects.data ? getPageInfo(projects.data) : undefined,
    },
    executeQuery,
  ] as const;
};

export const useProjectLazy = () => {
  const [project, queryExecute] = useSomethingLazily();

  const executeQuery = async (args: {
    accountId: number;
    projectId: number;
    includeRelated?: string[];
  }) => {
    const res = await queryExecute({
      api_endpoint: API_V3,
      endpoint: `/accounts/${args.accountId}/projects/${args.projectId}/`,
      params: {
        include_related: args.includeRelated
          ? JSON.stringify(args.includeRelated)
          : undefined,
      },
    });

    return {
      ...res,
      data: res.data?.data as Project,
      fetching: false,
    };
  };
  return [
    {
      ...project,
      data: project.data?.data as Project,
    },
    executeQuery,
  ] as const;
};

export const getProject = async (
  accountId: number,
  projectId: number,
  includeRelated: string[] = [],
): Promise<Project> => {
  const response = await axios.get<ApiV3Response<Project>>(
    `${API_V3}/accounts/${accountId}/projects/${projectId}/`,
    {
      params: {
        include_related: JSON.stringify(includeRelated),
      },
    },
  );

  return response?.data?.data;
};

export const getProjects = async (
  accountId: number,
  includeRelated: string[] = [],
): Promise<Project[]> => {
  return await getSomethingPaginated<Project>(
    API_V3,
    `/accounts/${accountId}/projects/`,
    { include_related: JSON.stringify(includeRelated) },
  );
};

export interface Project {
  id?: number | null;
  name: string;
  description?: string;
  account_id?: number;
  repository_id?: number | null;
  connection_id?: number | null;
  dbt_project_subdirectory?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  state?: any;
  connection?: any | null;
  repository?: any | null;
  skipped_setup?: boolean;
  group_permissions?: any[] | null;
  docs_job?: any;
  freshness_job?: any;
  docs_job_id?: number;
  freshness_job_id?: number;
  semantic_layer_config_id?: number | null;
  environments?: Environment[] | null;
}

// Helper functions to extract data and pagination info from API responses
export const getResults = <T>(data: any): T[] => {
  return data?.data || [];
};

export const getPageInfo = (data: any) => {
  if (!data?.extra?.pagination) return undefined;

  const { count, total_count } = data.extra.pagination;
  const offset = parseInt(data.extra.offset || '0', 10);

  return {
    hasNextPage: offset + count < total_count,
    endCursor: (offset + count).toString(),
    totalCount: total_count,
  };
};
